import React, { useRef, useEffect } from 'react';
import { useReducedMotion, useInView } from 'framer-motion';
import { motion } from '../../lib/motionShim';
/**
 * The Coach Console on the big screen — a laptop that opens as you scroll.
 *
 * The lid animation is deliberately plain JavaScript: a passive scroll
 * listener reads the laptop's viewport position and writes the transform
 * directly. No animation-library scroll pipeline involved — if the page
 * scrolls, the lid moves. Progress 0 = laptop entering the bottom of the
 * viewport (lid closed, screen dark); progress 1 = upper third (open, lit).
 */

const SCREEN_SRC = '/screens/coach-console.webp';

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

const useImageExists = (src, enabled = true) => {
  const [ok, setOk] = React.useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const im = new Image();
    im.onload = () => alive && setOk(true);
    im.onerror = () => alive && setOk(false);
    im.src = src;
    return () => {
      alive = false;
    };
  }, [src, enabled]);
  return ok;
};

const ConsoleShowcase = () => {
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);
  // The probe downloads the full console screenshot — wait until we're close
  const nearView = useInView(sectionRef, { once: true, margin: '800px' });
  const hasScreen = useImageExists(SCREEN_SRC, nearView);

  const laptopRef = useRef(null);
  const lidRef = useRef(null);
  const dimRef = useRef(null);

  useEffect(() => {
    if (!hasScreen || reduce) return;
    const laptop = laptopRef.current;
    const lid = lidRef.current;
    const dimEl = dimRef.current;
    if (!laptop || !lid || !dimEl) return;

    const update = () => {
      const rect = laptop.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // top edge travels from 95% of viewport height down to 32%
      const raw = (0.95 * vh - rect.top) / ((0.95 - 0.32) * vh);
      const t = easeOut(clamp(raw, 0, 1));

      lid.style.transform = `rotateX(${-78 * (1 - t)}deg)`;
      dimEl.style.opacity = String(0.85 * (1 - clamp((raw - 0.1) / 0.65, 0, 1)));
      laptop.style.transform = `translateY(${56 * (1 - t)}px) scale(${0.95 + 0.05 * t})`;
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [hasScreen, reduce]);

  return (
    <section
      id="console"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-ston-bg overflow-hidden"
      data-testid="console-showcase"
      style={hasScreen ? undefined : { minHeight: 400 }}
    >
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(147,51,234,0.09), rgba(147,51,234,0.045) 55%, transparent 78%)' }} />
      </div>

      <div className="relative max-w-[1100px] mx-auto px-6 md:px-12">
        {/* Heading */}
        <div className="text-center max-w-[680px] mx-auto">
          <motion.span
            className="section-label inline-block"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
          >
            THE CONSOLE
          </motion.span>
          <motion.h2
            className="mt-4 font-outfit font-extrabold italic uppercase leading-[1.02]"
            style={{ fontSize: 'clamp(30px, 4.2vw, 54px)', letterSpacing: '-0.025em' }}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="block text-ston-text">Your business.</span>
            <span className="block gradient-text">Full screen.</span>
          </motion.h2>
          <motion.p
            className="mt-5 text-[16.5px] text-ston-text-secondary leading-relaxed"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            The same command center, on the web — for the days you coach from a desk.
          </motion.p>
        </div>

        {/* The laptop — styles written directly by the scroll listener */}
        {hasScreen && (
        <div
          ref={laptopRef}
          className="mt-16 md:mt-20 mx-auto will-change-transform"
          // 100vw minus the container's px-6 padding and the base's 3% overhang per side
          style={{ width: 'min(760px, calc(100vw - 72px))' }}
        >
          <div style={{ perspective: 1700 }}>
            {/* Lid */}
            <div
              ref={lidRef}
              className="relative rounded-2xl border border-white/[0.09] overflow-hidden will-change-transform"
              style={{
                transformOrigin: 'bottom center',
                transform: reduce ? 'none' : 'rotateX(-78deg)',
                background: 'linear-gradient(160deg, #34343f 0%, #101015 40%, #26262f 100%)',
                padding: '1.6%',
                boxShadow: '0 40px 90px -30px rgba(0,0,0,0.85)',
              }}
            >
              <div className="relative rounded-xl overflow-hidden bg-ston-bg">
                <img
                  src={SCREEN_SRC}
                  alt="STON.FIT Coach Console on the web — command center with priority queue, health overview and daily stats"
                  className="w-full aspect-[16/10] object-cover object-top"
                  loading="lazy"
                  decoding="async"
                />
                {/* Camera */}
                <div
                  aria-hidden="true"
                  className="absolute top-[0.9%] left-1/2 -translate-x-1/2 h-[6px] w-[6px] rounded-full bg-black"
                >
                  <span className="absolute inset-[1.5px] rounded-full bg-[#1e2733]" />
                </div>
                {/* Power-on dim */}
                <div
                  ref={dimRef}
                  aria-hidden="true"
                  className="absolute inset-0 bg-black pointer-events-none"
                  style={{ opacity: reduce ? 0 : 0.85 }}
                />
                {/* Glare */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(115deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.015) 24%, transparent 38%)',
                  }}
                />
              </div>
            </div>

            {/* Base / deck */}
            <div className="relative mx-auto" style={{ width: '106%', marginLeft: '-3%' }}>
              <div
                className="h-[15px] rounded-b-2xl rounded-t-[3px]"
                style={{
                  background:
                    'linear-gradient(180deg, #3c3c47 0%, #23232c 35%, #14141a 100%)',
                  boxShadow:
                    '0 24px 50px -18px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.14)',
                }}
              >
                <div
                  className="mx-auto h-[7px] w-[13%] rounded-b-2xl"
                  style={{
                    background: 'linear-gradient(180deg, #0a0a0f, #1b1b23)',
                    boxShadow: 'inset 0 -1px 2px rgba(0,0,0,0.8)',
                  }}
                />
              </div>
              <div
                aria-hidden="true"
                className="absolute -bottom-8 left-[6%] right-[6%] h-10 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(ellipse 50% 50% at 50% 50%, rgba(0,0,0,0.6), transparent 70%)',
                  filter: 'blur(14px)',
                }}
              />
            </div>
          </div>
        </div>
        )}
      </div>
    </section>
  );
};

export default ConsoleShowcase;
